import { useEffect, useRef, useCallback } from 'react'
import VideoBackground from '../components/VideoBackground'
import './Works.css'
import './WorksHero.css'

interface Work {
  id: string
  title: string
  description: string
  backgroundImage: string
  youtubeUrl?: string
  soundcloudUrl?: string
  twitchUrl?: string
}

const works: Work[] = [
  {
    id: 'pulse-transmissions',
    title: 'PULSE TRANSMISSIONS',
    description: 'Cyber industrial hypnotic mixes. Clean blends, deep pressure, signature transitions.',
    backgroundImage: `url('/images/pulse.jpg')`,
    youtubeUrl: 'https://youtube.com/@nessakodo',
    soundcloudUrl: 'https://soundcloud.com/nessakodo',
  },
  {
    id: 'signal-sessions',
    title: 'SIGNAL SESSIONS',
    description: 'Long form transmissions for focus and reset. Ambient, generative, cinematic drift.',
    backgroundImage: `url('/images/signal.jpg')`,
    youtubeUrl: 'https://youtube.com/@nessakodo',
    soundcloudUrl: 'https://soundcloud.com/nessakodo',
    twitchUrl: 'https://twitch.tv/nessakodo',
  },
  {
    id: 'ritual-interfaces',
    title: 'RITUAL INTERFACES',
    description: 'Playtron, TouchMe, code instruments, biosignal experiments, live input turned into music.',
    backgroundImage: `url('/images/ritual.jpg')`,
    youtubeUrl: 'https://youtube.com/@nessakodo',
    soundcloudUrl: 'https://soundcloud.com/nessakodo',
    twitchUrl: 'https://twitch.tv/nessakodo',
  },
  {
    id: 'visual-worlds',
    title: 'VISUAL WORLDS',
    description: 'TouchDesigner visuals and overlays. Structured chaos, evolving textures, living movement.',
    backgroundImage: `url('/images/worlds.jpg')`,
    youtubeUrl: 'https://youtube.com/@nessakodo',
    twitchUrl: 'https://twitch.tv/nessakodo',
  },
]

const CARD_WIDTH = 400
const GAP = 40
const SET_WIDTH = works.length * (CARD_WIDTH + GAP)

const WorkCard = ({ work }: { work: Work }) => (
  <div className="work-item">
    <div className="work-image" style={{ backgroundImage: work.backgroundImage }}></div>
    <div className="work-content">
      <h3 className="work-title">{work.title}</h3>
      <p className="work-description">{work.description}</p>
      <div className="work-links">
        {work.youtubeUrl && (
          <a href={work.youtubeUrl} target="_blank" rel="noopener noreferrer" className="work-link">
            <i className="fab fa-youtube"></i>
            <span>youtube</span>
          </a>
        )}
        {work.soundcloudUrl && (
          <a href={work.soundcloudUrl} target="_blank" rel="noopener noreferrer" className="work-link">
            <i className="fab fa-soundcloud"></i>
            <span>soundcloud</span>
          </a>
        )}
        {work.twitchUrl && (
          <a href={work.twitchUrl} target="_blank" rel="noopener noreferrer" className="work-link">
            <i className="fab fa-twitch"></i>
            <span>twitch</span>
          </a>
        )}
      </div>
    </div>
  </div>
)

const Works = () => {
  const trackRef = useRef<HTMLDivElement>(null)
  const posRef = useRef(0)
  const speedRef = useRef(1)
  const targetSpeedRef = useRef(1)
  const rafRef = useRef<number>(0)

  const orbitWorks = [...works, ...works]

  useEffect(() => {
    const track = trackRef.current
    if (!track) return

    const pxPerFrame = SET_WIDTH / (28 * 60)

    const animate = () => {
      speedRef.current += (targetSpeedRef.current - speedRef.current) * 0.03
      posRef.current -= pxPerFrame * speedRef.current

      if (posRef.current <= -SET_WIDTH) {
        posRef.current += SET_WIDTH
      }

      track.style.transform = `translate3d(${posRef.current}px, 0, 0)`
      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  const handleMouseEnter = useCallback(() => {
    targetSpeedRef.current = 0.15
  }, [])

  const handleMouseLeave = useCallback(() => {
    targetSpeedRef.current = 1
  }, [])

  return (
    <main className="main-content sessions-page">
      <VideoBackground
        webmSrc="/videos/works-hero.webm"
        mp4Src="/videos/works-hero.mp4"
        poster="/images/works-hero-poster.jpg"
        className="works-hero"
      >
        <div className="works-hero-content">
          <h1 className="works-hero-title">sessions</h1>
        </div>
      </VideoBackground>

      <div className="featured-section">
        <div
          className="works-orbit"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="works-orbit-track" ref={trackRef}>
            {orbitWorks.map((work, i) => (
              <WorkCard key={`${work.id}-${i}`} work={work} />
            ))}
          </div>
        </div>

        <div className="works-mobile-list">
          {works.map(work => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      </div>
    </main>
  )
}

export default Works
